import { debounce } from 'helpers/Functional';
import { CARD_TYPE_EPISODE, CARD_TYPE_POSTER, isLive } from 'helpers/ContentHelper';
import {
  BUILD_VERSION,
  callTealiumEvent,
  Events,
  getTealiumSearchResultString,
  handleAnalytics,
  PageNames,
  SegmentReferrerProps,
  setTealiumProfileId,
  TEALIUM_EVENT_PREFIX,
  trackPageReferrerAnalyticsHook,
} from '@foxcorp/delta-web-analytics';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { getMinSearchLength } from 'helpers/ConfigHelper';
import { clearVideo as clearGlobalVideo } from 'components/VideoPlayerGlobal/Actions';
import { saveLiveVideo } from 'screens/Live/LiveActions';

const ALL_RESULTS = 'allResults';
const EPISODES = 'episodes';
const PERSONALITES = 'personalities';

/* globals EpgEnabled */

const trackResultSelected = ({ titleText, selectedTabName, searchProps = {}, genreFilterName }) => {
  const payload = {};
  const { searchValue, searchCount, searchCompleted } = searchProps;
  if (searchValue) payload.page_search_term = searchValue;
  if (searchCount) payload.page_num_search_results = searchCount;
  if (selectedTabName) payload.page_category_title = selectedTabName;
  if (titleText) payload.page_item_title = titleText;
  if (searchValue) {
    handleAnalytics({
      event: Events.SEARCH_RESULT_SELECTED,
      payload,
    });
  } else {
    handleAnalytics({
      event: Events.BROWSE_ITEM_SELECTED,
      payload: {
        page_category_title: selectedTabName,
        page_filter_title: genreFilterName,
        page_item_title: titleText,
      },
    });
  }
  if (!searchCompleted && Object.keys(searchProps).length) {
    handleAnalytics({
      event: Events.SEARCH_COMPLETED,
      payload: {
        page_search_term: searchValue?.trim(),
        page_num_search_results: searchCount,
      },
      tealiumPayload: {
        ...getCommonTealiumProps(),
      },
    });
  }
};

const onResultClick = ({ item, titleText, account, searchProps = {} }) => {
  const tabList = window.location.pathname.split('/');
  const genreFilterName = tabList[2];
  const selectedTabName = tabList[4] ? decodeURI(tabList[4]) : tabList[1];
  const itemIsLive = isLive(item?.['@type']);
  const {
    blockReferrerAnalyticsTracking,
    searchCount,
    searchValue,
    setTrackedAnalyticsReferrer,
    trackedAnalyticsReferrer,
  } = searchProps;
  trackResultSelected({
    genreFilterName,
    searchProps,
    selectedTabName,
    titleText,
  });
  if (!EpgEnabled && itemIsLive) {
    clearGlobalVideo({ source: 'HEADER_SEARCH' });
    saveLiveVideo(item);
  }
  let action = 'search_event';
  let profileId = account?.profileId;
  let data = setTealiumProfileId(profileId, {
    action,
    platform_version: BUILD_VERSION,
    search_term: searchValue,
    search_result_type: 'selected a displayed search result',
    num_search_results: searchCount,
    ...getCommonTealiumProps(),
  });
  if (searchValue?.length > 1) {
    callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
  }
  action = 'search_result_select';
  profileId = account?.profileId;
  const searchResultInfo = getTealiumSearchResultString({
    genreFilterName,
    isNation: true,
    itemIsLive,
    originalItem: item,
    selectedTabName,
    titleText,
  });
  data = setTealiumProfileId(profileId, {
    platform_version: BUILD_VERSION,
    action,
    search_result_info: searchResultInfo,
    ...getCommonTealiumProps(),
  });
  if (searchValue?.length >= getMinSearchLength()) {
    trackPageReferrerAnalyticsHook({
      pageName: PageNames.SEARCH,
      data: {
        [SegmentReferrerProps.NAME]: searchValue,
        [SegmentReferrerProps.POSITION]: 'None',
      },
      blockReferrerAnalyticsTracking,
      setTrackedAnalyticsReferrer,
      trackedAnalyticsReferrer,
    });
  } else {
    trackPageReferrerAnalyticsHook({
      name: PageNames.FIND,
      data: {
        [SegmentReferrerProps.NAME]: genreFilterName,
        [SegmentReferrerProps.POSITION]: selectedTabName,
      },
      blockReferrerAnalyticsTracking,
      setTrackedAnalyticsReferrer,
      trackedAnalyticsReferrer,
    });
  }
  callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
};

const isSearchResultsType = id => [ALL_RESULTS, EPISODES, PERSONALITES].includes(id);

const filterSearchResults = (results = []) =>
  results?.filter(item => isSearchResultsType(item?.id));

const isPropValuesEqual = (subject, target, propNames) =>
  propNames?.every(propName => subject?.[propName] === target?.[propName]);

const getUniqueItemsById = items => {
  const propNamesArray = ['id'];

  return items.filter(
    (item, index, array) =>
      index === array?.findIndex(foundItem => isPropValuesEqual(foundItem, item, propNamesArray)),
  );
};

const reduceBrowseResults = (result = []) => {
  const type = CARD_TYPE_POSTER;
  return {
    [result?.id]: {
      items: result?.member,
      next: result?.view?.next,
      type,
    },
  };
};

const reduceSearchResults = (results = []) =>
  results?.reduce(
    (acc, item) => ({
      ...acc,
      [item.id]: {
        items: item?.member ?? [],
        next: item?.view?.next ?? '',
        type: item?.id === EPISODES ? CARD_TYPE_EPISODE : CARD_TYPE_POSTER,
      },
    }),
    {},
  );

const transformSearchFilters = (filters = []) =>
  filters?.map(({ title = '', id = '', totalItems }) => {
    const headline = totalItems && id !== ALL_RESULTS ? `${title} (${totalItems})` : title;
    return {
      headline,
      id,
    };
  });

const getSelectedFilters = ({
  searchValue = '',
  searchFilters = [],
  browseFilters = [],
  fetchingSearch,
  previousItemsRef,
}) => {
  if (fetchingSearch) {
    return previousItemsRef?.current?.filters ?? [];
  }
  return searchValue ? searchFilters : browseFilters;
};

const getSelectedResults = ({
  searchValue = '',
  searchResults = {},
  browseResults = {},
  fetchingSearch,
  previousItemsRef,
}) => {
  if (fetchingSearch) {
    return previousItemsRef?.current?.results ?? {};
  }
  return searchValue ? searchResults : browseResults;
};

const scrollTop = debounce(() => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}, 500);

export {
  filterSearchResults,
  getSelectedFilters,
  getSelectedResults,
  getUniqueItemsById,
  isSearchResultsType,
  onResultClick,
  reduceBrowseResults,
  reduceSearchResults,
  scrollTop,
  transformSearchFilters,
};
